import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Select, Checkbox, Radio, Slider, Input } from 'antd';
import { InboxFilters } from '../../types';
import moment from 'moment';
import { Icon } from '@ant-design/compatible';

export const initialFilterValues: InboxFilters = {
  name: '',
  tag: '',
  flow: [],
  status: '',
  score: [],
  date: [],
  sort: [],
};

export const setFiltersOnMount = (query: URLSearchParams, setFilters: any) => {
  if (query) {
    const scoreGt = query.get('score.gt') ? parseFloat(query.get('score.gt') || '') * 100 : 0;
    const scoreLt = query.get('score.lt') ? parseFloat(query.get('score.lt') || '') * 100 : 0;
    setFilters((prev: InboxFilters) => ({
      ...prev,
      name: query.get('name') || '',
      tag: query.get('tag') || '',
      flow: query.get('flow')?.split(',') || [],
      status: query.get('status') || '',
      date:
        query.get('intervalStartDate') && query.get('intervalEndDate')
          ? [moment(query.get('intervalStartDate'), 'YYYY-MM-DD'), moment(query.get('intervalEndDate'), 'YYYY-MM-DD')]
          : [],
      score: query.get('score.gt') && query.get('score.lt') ? [scoreGt, scoreLt] : [],
      sort:
        query
          .get('sort')
          ?.split(',')
          .map(s => ({ field: s.split(':')[0], order: s.split(':')[1] === 'ASC' ? 'ascend' : 'descend' })) || [],
    }));
  }
};

export const renderFilterString = (filters: InboxFilters): string => {
  let queryParams = [];
  if (filters.name) {
    queryParams.push('name=' + filters.name);
  }
  if (filters.tag) {
    queryParams.push('tag=' + filters.tag);
  }
  if (filters.flow.length) {
    queryParams.push('flow=' + filters.flow.map((f, i) => `${i !== 0 ? ',' : ''}${f}`).join(''));
  }
  if (filters.status) {
    queryParams.push('status=' + filters.status);
  }
  if (filters.score.length) {
    queryParams.push('score.gt=' + filters.score[0] / 100 + '&score.lt=' + filters.score[1] / 100);
  }
  if (filters.date.length) {
    const startDate = filters.date[0].format('YYYY-MM-DD');
    const endDate = filters.date[1].format('YYYY-MM-DD');
    queryParams.push('intervalStartDate=' + startDate + '&intervalEndDate=' + endDate);
  }
  if (filters.sort.length) {
    queryParams.push(
      'sort=' +
        filters.sort.map((s, i) => {
          const order = s.order === 'ascend' ? 'ASC' : 'DESC';
          return `${s.field}:${order}`;
        })
    );
  }
  if (queryParams.length) {
    return '&' + queryParams.map((param, i, arr) => param + (i === arr.length - 1 ? '' : '&')).join('');
  } else {
    return '';
  }
};

export const NameFilter = ({ filters, setFilters }: { filters: InboxFilters; setFilters: any }) => {
  const [value, setValue] = useState<string>(filters.name);

  useEffect(() => {
    setTimeout(() => {
      setFilters((prev: any) => ({ ...prev, name: value }));
    }, 400);
  }, [value, setFilters]);

  return (
    <div style={{ padding: '4px 0' }}>
      <Input
        style={{ margin: '8px 12px', width: 'auto' }}
        type="text"
        value={value}
        onChange={e => {
          const { value } = e.target;
          setValue(value);
        }}
      />
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button size="small" type="link" onClick={() => setValue('')}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const TagFilter = ({ filters, setFilters }: { filters: InboxFilters; setFilters: any }) => {
  const [value, setValue] = useState<string>(filters.tag);

  useEffect(() => {
    setTimeout(() => {
      setFilters((prev: any) => ({ ...prev, tag: value }));
    }, 400);
  }, [value, setFilters]);

  return (
    <div style={{ padding: '4px 0' }}>
      <Input
        style={{ margin: '8px 12px', width: 'auto' }}
        type="text"
        value={value}
        onChange={e => {
          const { value } = e.target;
          setValue(value);
        }}
      />
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button size="small" type="link" onClick={() => setValue('')}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const DateFilter = ({ filters, setFilters }: { filters: any; setFilters: any }) => {
  const { RangePicker } = DatePicker;
  const { Option } = Select;

  const [datePreset, setDatePreset] = useState('');

  return (
    <div>
      <RangePicker
        style={{ padding: 9 }}
        format="YYYY-MM-DD"
        value={filters.date}
        onChange={e => {
          setFilters((prev: any) => ({ ...prev, date: e !== null ? e : [] }));
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid #e8e8e8',
          padding: '7px 8px',
        }}>
        <Select
          style={{ width: 150 }}
          placeholder="Select a preset"
          value={datePreset}
          onChange={(e: any) => {
            setDatePreset(e);
            setFilters((prev: any) => ({ ...prev, date: e ? [moment().subtract(e, 'months'), moment()] : [] }));
          }}>
          <Option value="">Select a preset</Option>
          <Option value="1">Last 1 month</Option>
          <Option value="2">Last 2 months</Option>
        </Select>
        <div>
          <Button
            type="link"
            size="small"
            className="text-toolbox-primary w-12"
            onClick={() => {
              setDatePreset('');
              setFilters((prev: any) => ({ ...prev, date: [] }));
            }}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export const FlowFilter = ({
  filters,
  setFilters,
  flowOptions,
}: {
  filters: any;
  setFilters: any;
  flowOptions: any;
}) => {
  const toggleFlow = (e: any, flow: string) => {
    if (e.target.checked) {
      setFilters((prev: any) => ({ ...prev, flow: [...prev.flow, flow] }));
    } else {
      setFilters((prev: any) => ({ ...prev, flow: [...prev.flow.filter((v: any) => v !== flow)] }));
    }
  };

  return (
    <div style={{ padding: '4px 0', display: 'flex', flexDirection: 'column' }}>
      {flowOptions.map((flow: any) => (
        <Checkbox
          style={{ padding: '5px 12px', marginLeft: 0 }}
          key={flow.multiflow_name}
          checked={filters.flow.includes(flow.multiflow_name)}
          onChange={e => toggleFlow(e, flow.multiflow_name)}>
          {flow.multiflow_name}
        </Checkbox>
      ))}
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button type="link" size="small" onClick={() => setFilters((prev: any) => ({ ...prev, flow: [] }))}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const StatusFilter = ({ filters, setFilters }: { filters: any; setFilters: any }) => {
  return (
    <div style={{ padding: '4px 0' }}>
      <Radio.Group
        style={{ display: 'flex', flexDirection: 'column' }}
        value={filters.status}
        onChange={e => setFilters((prev: any) => ({ ...prev, status: e.target.value }))}>
        <Radio style={{ padding: '5px 12px' }} value="NEW">
          New
        </Radio>
        <Radio style={{ padding: '5px 12px' }} value="STAGED">
          Staged
        </Radio>
      </Radio.Group>
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button type="link" size="small" onClick={() => setFilters((prev: any) => ({ ...prev, status: '' }))}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const ScoreFilter = ({ filters, setFilters }: { filters: any; setFilters: any }) => {
  const [sliderValues, setSliderValues] = useState<[number, number] | undefined>([0, 0]);

  useEffect(() => {
    if (filters.score.length) {
      setSliderValues(filters.score);
    } else {
      setSliderValues([0, 0]);
    }
  }, [filters]);

  return (
    <div style={{ width: '260px', padding: '4px 0' }}>
      <Slider
        style={{ height: '30px', margin: '12px 20px' }}
        range
        step={5}
        marks={{ 0: '0', 25: '25', 50: '50', 75: '75', 100: '100' }}
        value={sliderValues}
        onChange={value => setSliderValues(value)}
      />
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button type="link" size="small" onClick={() => setFilters((prev: any) => ({ ...prev, score: sliderValues }))}>
          Ok
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setFilters((prev: any) => ({ ...prev, score: [] }));
            setSliderValues([0, 0]);
          }}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const SorterToggle = ({
  filterKey,
  label,
  filters,
  setFilters,
}: {
  filterKey: string;
  label: string;
  filters: any;
  setFilters: any;
}) => {
  return (
    <Button
      onClick={() => {
        if (!filters.sort.find((s: any) => s.field === filterKey)) {
          setFilters((prev: InboxFilters) => ({
            ...prev,
            sort: [...prev.sort, { field: filterKey, order: 'ascend' }],
          }));
        } else if (filters.sort.find((s: any) => s.field === filterKey && s.order === 'descend')) {
          setFilters((prev: InboxFilters) => ({
            ...prev,
            sort: prev.sort.filter(s => s.field !== filterKey),
          }));
        } else {
          setFilters((prev: InboxFilters) => ({
            ...prev,
            sort: prev.sort.map(s => {
              if (s.field === filterKey) {
                return { field: s.field, order: 'descend' };
              } else {
                return s;
              }
            }),
          }));
        }
      }}
      className="flex justify-between items-center">
      {label}
      <div className="flex flex-col justify-center items-center ml-2">
        <Icon
          style={{
            marginBottom: '-5px',
            color: filters.sort.find((s: any) => s.field === filterKey && s.order === 'ascend') ? '#00b9ff' : '#ddd',
          }}
          type="caret-up"
        />
        <Icon
          style={{
            color: filters.sort.find((s: any) => s.field === filterKey && s.order === 'descend') ? '#00b9ff' : '#ddd',
          }}
          type="caret-down"
        />
      </div>
    </Button>
  );
};

export const createQueryStringFirstTimeForPageFetch = (query: URLSearchParams) => {
  const string =
    (query.get('name') ? `&name=${query.get('name')}` : '') +
    (query.get('tag') ? `&tag=${query.get('tag')}` : '') +
    (query.get('status') ? `&status=${query.get('status')}` : '') +
    (query.get('flow') ? `&flow=${query.get('flow')}` : '') +
    (query.get('score.gt') ? `&score.gt=${query.get('score.gt')}` : '') +
    (query.get('score.lt') ? `&score.lt=${query.get('score.lt')}` : '') +
    (query.get('intervalStartDate') ? `&intervalStartDate=${query.get('intervalStartDate')}` : '') +
    (query.get('intervalEndDate') ? `&intervalEndDate=${query.get('intervalEndDate')}` : '') +
    (query.get('sort') ? `&sort=${query.get('sort')}` : '');
  return string;
};
